<script setup lang="ts">
import { arrayToMap } from "@/api/query";
import { useSpaces } from "@/api/space-query";
import { useStations } from "@/api/station-query";
import AppDataTable from "@/components/common/AppDataTable.vue";
import ColumnName from "@/components/common/ColumnName.vue";
import DesktopOnly from "@/components/common/DesktopOnly.vue";
import Frame from "@/components/common/Frame.vue";
import CreateStationDialog from "@/components/dialogs/CreateStationDialog.vue";
import { icRemoteOutline } from "@/components/icons";
import IntegrityNavButton from "@/components/navigation/IntegrityNavButton.vue";
import MapNavButton from "@/components/navigation/MapNavButton.vue";
import SettingNavButton from "@/components/navigation/SettingNavButton.vue";
import FeatureGuard from "@/components/permission/FeatureGuard.vue";
import ScopeGuard from "@/components/permission/ScopeGuard.vue";
import VisibilityGuard from "@/components/permission/VisibilityGuard.vue";
import SiteLinkedBadge from "@/components/sites/SiteLinkedBadge.vue";
import NavBarStatus from "@/components/workspace/NavBarStatus.vue";
import { useFavoriteStationSelection } from "@/composable/favorite-selection";
import Drawer from "@/layouts/drawer/Drawer.vue";
import DrawerLayout from "@/layouts/drawer/DrawerLayout.vue";
import { DrawerSelection } from "@/layouts/drawer/use-drawer-data";
import {
	StationStatusStateExtended,
	useDrawerState,
	useDrawerStore,
	useLiveStatusStore,
} from "@/stores";
import { ObjectSummary } from "@soft-db/monitoring-api-client";
import {
	Badge,
	Button,
	ColumnSelection,
	Icon,
	LinkButton,
	makeColumnHelper,
	makeFacetedMapping,
} from "@soft-db/ui";
import { storeToRefs } from "pinia";

type StationType = "pro-station" | "watch" | "beamforming" | "unknown";

type StationRowData = {
	id: string;
	name: string;
	description: string;
	serialNumber: string;
	stationType: StationType;
	site: ObjectSummary | null;
	space: ObjectSummary | null;
	connected: boolean;
	status: StationStatusStateExtended[];
	inFavorite: boolean;
};

const { toggleDrawer } = useDrawerStore();

const { t } = useI18n();

const router = useRouter();
const route = useRoute();

function changePage(selection: DrawerSelection) {
	router.push({
		name: "stations-id-index",
		params: { id: selection.id },
		query: { ...route.query },
	});
	toggleDrawer();
}

const { stations, isLoading: stationsLoading } = useStations();
const { spaces, isLoading: spacesLoading } = useSpaces();

const spacesMap = computed(() => arrayToMap(spaces.value || []));
const isLoading = logicOr(stationsLoading, spacesLoading);

const createStationDialog = ref(false);
function openCreateStationDialog() {
	createStationDialog.value = true;
}

function goToStation(id: string) {
	router.push({ name: "stations-id-index", params: { id: id } });
}

function inferStationType(sn: string): StationType {
	if (sn.startsWith("PM")) {
		return "watch";
	}

	if (sn.startsWith("M") || sn.startsWith("PS")) {
		return "pro-station";
	}

	if (sn.startsWith("BF")) {
		return "beamforming";
	}

	return "unknown";
}

const statusStore = useLiveStatusStore();

const stationRows = computed<StationRowData[]>(() => {
	if (isLoading.value || !stations.value) return [];

	return stations.value.map((station) => ({
		id: station.id,
		name: station.name,
		description: station.description,
		serialNumber: station.serialNumber,
		stationType: inferStationType(station.serialNumber),
		status: statusStore.searchableStatusState("station", station.id),
		site: station.site,
		space: station.space
			? { id: station.space, name: spacesMap.value.get(station.space)?.name || "" }
			: null,
		connected: !!station.site,
		inFavorite: station.inFavorite,
	}));
});

const selection = useFavoriteStationSelection(stations);

const filtersDef = computed(() => {
	return makeFacetedMapping<StationRowData>({
		status: {
			name: t("common.status"),
			allowMultiple: false,
			choices: {
				online: {
					name: t("live-status.online"),
				},
				waiting: {
					name: t("live-status.idle"),
				},
				"recently-offline": {
					name: t("live-status.recentlyOffline"),
					hint: t("live-status.recentlyOfflineHelp"),
				},
				offline: {
					name: t("live-status.offline"),
				},
				none: {
					name: t("common.notAvailable"),
				},
			},
		},
		stationType: {
			name: t("station.stationType"),
			allowMultiple: true,
			choices: {
				watch: {
					name: "Watch",
				},
				"pro-station": {
					name: "ProStation",
				},
				beamforming: {
					name: t("station.acousticRadar"),
				},
				unknown: {
					name: t("common.unknown"),
				},
			},
		},
		connected: {
			name: t("common.linked"),
			hint: t("common.linkedHelp"),
			choices: {
				true: {
					name: t("common.linked"),
				},
				false: {
					name: t("common.notLinked"),
				},
			},
		},
		inFavorite: {
			name: t("common.favorite"),
			choices: {
				true: {
					name: t("common.inFavorite"),
				},
				false: {
					name: t("common.notInFavorite"),
				},
			},
		},
		space: {
			name: t("object.space"),
			allowMultiple: true,
			allowSearch: true,
			choices: { "": { name: t("space.noSpace") } },
		},
	});
});

const drawerState = useDrawerState();
const { filter } = storeToRefs(drawerState);

const fieldFilters = drawerState.getFilters(Object.keys(filtersDef.value));

// We reset filters on page load, to get a fresh state
// drawerState.clear();

const { Column } = makeColumnHelper<StationRowData>();
</script>

<template>
	<DrawerLayout mobileOnly>
		<template #drawer>
			<Drawer :objects="['station']" @click="changePage" />
		</template>
		<Frame class="px-0!" topMargin>
			<AppDataTable
				v-model:filter="filter"
				v-model:fieldFilters="fieldFilters"
				:data="stationRows"
				:loading="isLoading"
				:filtersDef="filtersDef"
				stateStorage="url"
			>
				<template #title>
					<h1 class="flex items-center gap-1 text-center capitalize">
						<Icon :d="icRemoteOutline" />
						{{ t("object.station", 2) }}
					</h1>
				</template>
				<template #action>
					<VisibilityGuard permission="station.create" scope="host">
						<Button variant="outline" size="sm" color="default" @click="openCreateStationDialog">
							{{ t("actions.createStation") }}
						</Button>
					</VisibilityGuard>
				</template>
				<template #columns>
					<FeatureGuard feature="favorite">
						<ColumnSelection v-model:selection="selection" selectionMode="multiple" />
					</FeatureGuard>

					<ScopeGuard scope="distributor">
						<Column
							v-slot="{ row: { original: station } }"
							field="name"
							:header="t('common.name')"
							enableSorting
							enableGlobalFilter
							mobileMain
						>
							<ColumnName
								:id="station.id"
								:name="station.name"
								:description="station.description"
							/>
						</Column>

						<Column
							field="serialNumber"
							:header="t('station.serialNumber')"
							enableSorting
							enableGlobalFilter
						/>

						<template #fallback>
							<Column
								v-slot="{ row: { original: station } }"
								field="serialNumber"
								:header="t('station.serialNumber')"
								enableSorting
								enableGlobalFilter
								mobileMain
							>
								<h5>{{ station.serialNumber }}</h5>
							</Column>
						</template>
					</ScopeGuard>

					<Column
						v-slot="{ getValue }"
						field="stationType"
						:header="t('station.stationType')"
						enableColumnFilter
					>
						<Badge class="truncate rounded-md" size="sm" color="default" variant="soft">
							{{ filtersDef.stationType?.choices?.[getValue()]?.name }}
						</Badge>
					</Column>

					<DesktopOnly>
						<ScopeGuard scope="distributor">
							<Column
								id="space"
								v-slot="{ row: { original: station } }"
								:fieldFn="(station) => station.space?.name || ''"
								:header="t('object.space')"
								enableColumnFilter
							>
								<div class="flex flex-wrap gap-1">
									<LinkButton
										v-if="station.space"
										color="primary"
										variant="soft"
										size="xs"
										:to="{ name: 'spaces-id', params: { id: station.space.id } }"
									>
										{{ station.space.name }}
									</LinkButton>
								</div>
							</Column>
						</ScopeGuard>
					</DesktopOnly>

					<Column
						id="site"
						v-slot="{ row: { original: station } }"
						:fieldFn="(d) => d.site?.name || ''"
						:header="t('object.project', 1)"
						enableSorting
						enableGlobalFilter
					>
						<SiteLinkedBadge :site="station.site" />
					</Column>

					<Column v-slot="{ row }" field="status" enableColumnFilter header="" shrink>
						<NavBarStatus class="float-right" sourceType="station" :sourceId="row.original.id" />
					</Column>
					<ScopeGuard scope="distributor">
						<Column v-slot="{ row: { original: station } }" shrink>
							<div class="flex justify-end gap-1">
								<MapNavButton
									sourceType="station"
									:sourceId="station.id"
									:inFavorite="station.inFavorite"
									size="sm"
								/>
								<IntegrityNavButton sourceType="station" :sourceId="station.id" size="sm" />
								<SettingNavButton sourceType="station" :sourceId="station.id" size="sm" />
							</div>
						</Column>
					</ScopeGuard>

					<!-- Hidden columns, added for search -->
					<Column hidden field="id" enableGlobalFilter />
					<Column hidden field="description" enableGlobalFilter />
					<Column hidden field="connected" enableColumnFilter />
					<Column hidden field="inFavorite" enableColumnFilter />
				</template>
			</AppDataTable>
		</Frame>
		<CreateStationDialog v-model:dialog="createStationDialog" @created="goToStation" />
	</DrawerLayout>
</template>
